import React from 'react';
import { sectionOrder } from '../../content/publicPages';
import { renderSections } from '../../content/sectionTone';
import { usePublicPage } from '../../hooks/usePublicPage';
import {
  HeroSection,
  MarketingLayerBand,
  MarketingMadeBand,
  MarketingPossibilitiesBand,
  ServiceCategories,
  FeaturedVendors,
  VendorSpotlight,
  VendorServices,
  MarketingStepsBand,
  MarketingExtraordinaryBand,
  MarketingAudienceBand,
  MarketingStoriesBand,
  MarketingDestinationsBand,
  MarketingHostCtaBand,
  MarketingClosingCta,
} from '../../components/home';
import CategoryServicesSkeleton from '../../components/shared/CategoryServicesSkeleton';
import { useHomeCategories } from '../../hooks/useHomeCategories';
import { useCategoryServices } from '../../hooks/useCategoryServices';
import useUserLocation from '../../hooks/useUserLocation';

const CategoryVendorServices = ({
  category,
  className = '',
  priceLabel = 'starting from',
  linkPrefix,
  userLocation,
}) => {
  const { services, isLoading, error } = useCategoryServices(category.id, {
    location: userLocation,
  });

  return (
    <VendorServices
      title={category.category_name || 'Services'}
      data={services}
      isLoading={isLoading}
      error={error}
      categorySlug={category.id}
      linkPrefix={linkPrefix || '/services'}
      priceLabel={priceLabel}
      className={className}
      emptyMessage={`No ${category.category_name?.toLowerCase()} services available`}
      emptyDescription="Check back later for new services"
    />
  );
};

/**
 * Home: Peerspace marketplace flow × Shopify marketing texture.
 * Editorial bands use dummy content; inventory rails stay live.
 */
const ClientHome = () => {
  const { categories, isLoading: categoriesLoading } = useHomeCategories();
  const { userLocation } = useUserLocation();
  const { saved } = usePublicPage('home');
  const order = sectionOrder(saved, 'home');

  const inventory = (
    <div key="inventory" className="overflow-hidden bg-m-canvas-light">
        {categoriesLoading ? (
          <CategoryServicesSkeleton sections={3} />
        ) : categories.length > 0 ? (
          categories.map((category, index) => {
            const shouldShowSpotlight = index === 3 && categories.length > 3;

            return (
              <React.Fragment key={category.id}>
                <CategoryVendorServices
                  category={category}
                  className={index % 2 === 1 ? 'bg-m-canvas-light' : 'bg-m-surface'}
                  priceLabel="starting from"
                  linkPrefix="/services"
                  userLocation={userLocation}
                />
                {shouldShowSpotlight ? <VendorSpotlight /> : null}
              </React.Fragment>
            );
          })
        ) : (
          <VendorSpotlight />
        )}

        {categories.length > 0 && categories.length <= 3 ? <VendorSpotlight /> : null}
    </div>
  );

  const blocks = {
    hero: <HeroSection key="hero" />,
    layer: <MarketingLayerBand key="layer" />,
    made: <MarketingMadeBand key="made" />,
    why: <MarketingPossibilitiesBand key="why" />,
    catalog: (
      <div key="catalog" className="overflow-hidden bg-m-canvas-light">
        <ServiceCategories />
        <FeaturedVendors />
      </div>
    ),
    steps: <MarketingStepsBand key="steps" />,
    slides: <MarketingExtraordinaryBand key="slides" />,
    inventory,
    audience: <MarketingAudienceBand key="audience" />,
    stories: <MarketingStoriesBand key="stories" />,
    destinations: <MarketingDestinationsBand key="destinations" />,
    host: <MarketingHostCtaBand key="host" />,
    closing: <MarketingClosingCta key="closing" />,
  };

  return <>{renderSections(order, blocks, saved)}</>;
};

export default ClientHome;
